import React from 'react';

/**
 * Minimal markdown renderer for assistant replies.
 *
 * Covers what the model actually emits: ATX headings, bold/italic/inline code,
 * links, pipe tables, ordered and unordered lists, and blank-line-separated
 * paragraphs. Anything else is shown as literal text rather than silently
 * dropped.
 *
 * Output is built as React elements — there is no dangerouslySetInnerHTML
 * anywhere in here, so model output cannot inject markup. Keep it that way.
 *
 * If you later need blockquotes or fenced code blocks, swap this for
 * `react-markdown` rather than growing it much further.
 */

// Captures the delimiters so String.split keeps them as tokens. Links come
// first so their bracketed label isn't chewed up by the emphasis patterns.
const INLINE_PATTERN =
    /(\[[^\]]*\]\([^)]*\)|\*\*[^*]+\*\*|`[^`]+`|\*[^*\n]+\*)/g;

/**
 * Only http(s) and mailto links are rendered as anchors.
 *
 * This is model output, so a `javascript:` or `data:` href must never reach the
 * DOM. Anything else falls back to plain text rather than being silently
 * dropped, so the user still sees what was written.
 */
const safeHref = (raw) => {
    const href = String(raw ?? '').trim();
    return /^(https?:|mailto:)/i.test(href) ? href : null;
};

/**
 * Normalises Unicode lookalikes in model output.
 *
 * Some models — gpt-oss-120b notably — emit non-breaking hyphens and narrow
 * no-break spaces to stop dates and names wrapping. They render identically to
 * their ASCII counterparts but are different characters, so "2026\u2011 09\u201114"
 * will not match a search for "2026-09-14", and an activity name copied out of
 * the chat will not match the one in Adobe.
 *
 * Deliberately narrow. En dashes and em dashes are left alone: they are real
 * punctuation, and this data uses "\u2014" to mean an empty cell, so rewriting
 * them would change meaning rather than restore it. Smart quotes are left alone
 * for the same reason.
 */
const normalizeText = (text) =>
    String(text ?? '')
        // Non-breaking / typographic spaces -> a plain space.
        .replace(/[\u00a0\u2007\u2009\u200a\u202f]/g, ' ')
        // Zero-width characters: invisible, but they break search and copy.
        .replace(/[\u200b\u200c\u200d\ufeff]/g, '')
        // Hyphen lookalikes -> ASCII hyphen-minus.
        .replace(/[\u2010\u2011]/g, '-')
        // Strip any stray thinking tags from markdown display
        .replace(/<\/?\s*think\s*>/gi, '');

const renderInline = (text, keyPrefix) => {
    const parts = text.split(INLINE_PATTERN).filter(Boolean);

    return parts.map((part, index) => {
        const key = `${keyPrefix}-${index}`;

        const link = /^\[([^\]]*)\]\(([^)]*)\)$/.exec(part);
        if (link) {
            const href = safeHref(link[2]);
            const label = link[1] || href || '';
            return href ? (
                <a
                    key={key}
                    className="sai-md__link"
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    {label}
                </a>
            ) : (
                label
            );
        }

        if (part.length > 4 && part.startsWith('**') && part.endsWith('**')) {
            return <strong key={key}>{part.slice(2, -2)}</strong>;
        }
        if (part.length > 2 && part.startsWith('`') && part.endsWith('`')) {
            return <code key={key} className="sai-md__code">{part.slice(1, -1)}</code>;
        }
        if (part.length > 2 && part.startsWith('*') && part.endsWith('*')) {
            return <em key={key}>{part.slice(1, -1)}</em>;
        }
        return part;
    });
};

/** A markdown separator cell: ---, :---, ---:, :---: */
const isSeparatorCell = (cell) => /^:?-{2,}:?$/.test(cell);

const looksLikeTableLine = (line) => line.startsWith('|') && line.includes('|', 1);

/**
 * Parses pipe-table lines into { headers, rows }.
 *
 * Works off one flat list of cells rather than line-by-line, because models do
 * not reliably put each row on its own line — gpt-oss-120b returns the entire
 * table as a single line. Joining the collected lines and splitting on every
 * pipe yields the same cell sequence either way: the junction between two rows
 * ("… | | 1277992 |") produces an empty entry, which is filtered out along with
 * the leading and trailing ones.
 *
 * The separator row is what tells us the column count, so a table without one
 * is rejected and falls back to a paragraph.
 */
const parseTable = (tableLines) => {
    const cells = tableLines
        .join(' ')
        .split('|')
        .map((cell) => cell.trim())
        .filter(Boolean);

    const firstSeparator = cells.findIndex(isSeparatorCell);
    if (firstSeparator < 1) return null;

    const columnCount = firstSeparator;
    const headers = cells.slice(0, columnCount);

    // Every separator cell must be one, or we have mis-detected the boundary.
    const separators = cells.slice(columnCount, columnCount * 2);
    if (separators.length !== columnCount || !separators.every(isSeparatorCell)) {
        return null;
    }

    const body = cells.slice(columnCount * 2);
    const rows = [];
    for (let i = 0; i < body.length; i += columnCount) {
        const row = body.slice(i, i + columnCount);
        // Pad a short trailing row rather than dropping it.
        while (row.length < columnCount) row.push('');
        rows.push(row);
    }

    return { headers, rows };
};

const parseBlocks = (markdown) => {
    const lines = markdown.replace(/\r\n/g, '\n').split('\n');
    const blocks = [];

    let paragraph = [];
    let list = null;
    let tableLines = [];

    const flushParagraph = () => {
        if (paragraph.length) {
            blocks.push({ type: 'paragraph', text: paragraph.join(' ') });
            paragraph = [];
        }
    };

    const flushList = () => {
        if (list) {
            blocks.push(list);
            list = null;
        }
    };

    const flushTable = () => {
        if (!tableLines.length) return;

        const table = parseTable(tableLines);
        if (table) {
            blocks.push({ type: 'table', ...table });
        } else {
            // Not a real table — keep the text rather than losing it.
            blocks.push({ type: 'paragraph', text: tableLines.join(' ') });
        }
        tableLines = [];
    };

    lines.forEach((line) => {
        const trimmed = line.trim();

        if (!trimmed) {
            flushParagraph();
            flushList();
            flushTable();
            return;
        }

        if (looksLikeTableLine(trimmed)) {
            flushParagraph();
            flushList();
            tableLines.push(trimmed);
            return;
        }
        flushTable();

        const heading = /^(#{1,6})\s+(.*)$/.exec(trimmed);
        if (heading) {
            flushParagraph();
            flushList();
            blocks.push({
                type: 'heading',
                level: heading[1].length,
                text: heading[2],
            });
            return;
        }

        const ordered = /^\d+[.)]\s+(.*)$/.exec(trimmed);
        if (ordered) {
            flushParagraph();
            if (!list || !list.ordered) {
                flushList();
                list = { type: 'list', ordered: true, items: [] };
            }
            list.items.push(ordered[1]);
            return;
        }

        // Leading "**bold**" can't match here: [-*+] consumes one '*', then
        // \s+ fails against the second one.
        const unordered = /^[-*+]\s+(.*)$/.exec(trimmed);
        if (unordered) {
            flushParagraph();
            if (!list || list.ordered) {
                flushList();
                list = { type: 'list', ordered: false, items: [] };
            }
            list.items.push(unordered[1]);
            return;
        }

        flushList();
        paragraph.push(trimmed);
    });

    flushParagraph();
    flushList();
    flushTable();

    return blocks;
};

const Markdown = ({ content }) => {
    if (!content) return null;

    const blocks = parseBlocks(normalizeText(content));

    return (
        <div className="sai-md">
            {blocks.map((block, index) => {
                const key = `block-${index}`;

                if (block.type === 'heading') {
                    // Clamp to h4-h6: the panel already owns h2, and the reply
                    // shouldn't outrank it in the document outline.
                    const Tag = `h${Math.min(block.level + 3, 6)}`;
                    return (
                        <Tag
                            key={key}
                            className={`sai-md__heading sai-md__heading--${block.level}`}
                        >
                            {renderInline(block.text, key)}
                        </Tag>
                    );
                }

                if (block.type === 'table') {
                    return (
                        // Wide tables scroll inside their own box; the panel
                        // itself must never scroll sideways.
                        <div key={key} className="sai-md__table-wrap">
                            <table className="sai-md__table">
                                <thead>
                                    <tr>
                                        {block.headers.map((header, i) => (
                                            <th key={`${key}-h-${i}`}>
                                                {renderInline(header, `${key}-h-${i}`)}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {block.rows.map((row, rowIndex) => (
                                        <tr key={`${key}-r-${rowIndex}`}>
                                            {row.map((cell, i) => (
                                                <td key={`${key}-r-${rowIndex}-${i}`}>
                                                    {renderInline(
                                                        cell,
                                                        `${key}-r-${rowIndex}-${i}`
                                                    )}
                                                </td>
                                            ))}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    );
                }

                if (block.type === 'list') {
                    const Tag = block.ordered ? 'ol' : 'ul';
                    return (
                        <Tag key={key} className="sai-md__list">
                            {block.items.map((item, itemIndex) => (
                                <li key={`${key}-${itemIndex}`} className="sai-md__item">
                                    {renderInline(item, `${key}-${itemIndex}`)}
                                </li>
                            ))}
                        </Tag>
                    );
                }

                return (
                    <p key={key} className="sai-md__paragraph">
                        {renderInline(block.text, key)}
                    </p>
                );
            })}
        </div>
    );
};

export default Markdown;
